// A room's panes, openings and doors as the solid pieces they make of its walls
// (docs/claude/rooms.md, *A room feature's wall pieces*): the glass, the leaf,
// the wall under a sill and over a head, the header over a doorway, a
// partition's band under and over its pane and over its doorway, what a part's
// pocket leaves behind it — and each hole, which no mesh fills. Their heights
// are resolved here once, under a flat or a shaped ceiling: the 3D view meshes
// these pieces, its pick volumes are the holes, and a section's partitions are
// cut from the same bands. DOM-free.

import type {
  GlassSegment,
  InnerWall,
  ProductInstance,
  SaunaDoor,
  Vec2,
} from '../shared/types';
import type {
  SecPart,
  SecSrc,
} from './section';
import {
  isPolyRoom,
  pocketBacks,
} from './polyRoom';
import {
  doorGlassSpans,
  glassPolysLocal,
  openingBandLocal,
} from './glass';
import {
  doorParts,
  roomDoors,
} from './door';
import {
  doorTop,
  externalHeight,
  glassPaneBase,
  glassPaneTop,
  innerWallBase,
  innerWallTop,
  innerWallTopAt,
  openingArchitrave,
  openingSill,
  roomBase,
} from './heights';
import {
  type CeilingField,
  isFlatCeiling,
  minUnderOver,
  resolveCeiling,
  wallTopAt,
} from './ceiling';
import { instanceToWorld } from './geometry';
import { glazingOfPane } from './roomGlazing';
import { roomDoor } from './roomDoor';

/** What a piece is. */
export type FeaturePieceRole =
  | 'glass'
  | 'header'
  | 'hole'
  | 'leaf'
  | 'lintel'
  | 'partitionBand'
  | 'pocketBack'
  | 'sill';

/** Which pieces are built (meshed): every one but a hole, which is the space
 *  between them — a pick volume. */
export const BUILT: Record<FeaturePieceRole, boolean> = {
  glass: true,
  header: true,
  hole: false,
  leaf: true,
  lintel: true,
  partitionBand: true,
  pocketBack: true,
  sill: true,
};

/** What a piece is built of: the room's wall, glass in its own colour (a pane's,
 *  a door leaf's: none), or the partition it stands in. */
export type PieceStuff = { kind: 'wall' } | { kind: 'glass'; color?: string } | { kind: 'partition'; iw: InnerWall };

export interface FeaturePiece {
  role: FeaturePieceRole;
  /** The feature that makes it. */
  feature: { kind: 'door' | 'opening' | 'pane'; id?: string };
  /** What a click on it takes: the feature's own (its glass, a leaf, a hole),
   *  its partition's (a band), or null — the room's wall. Null too while the
   *  feature has no id (it is then its room's). */
  part: SecPart | null;
  /** A plan ring, in the room's LOCAL frame. */
  ring: Vec2[];
  /** Its bottom and its top, mm above the room's floor. */
  z0: number;
  z1: number;
  /** Under a shaped ceiling: its top is the lower of `z1` and the wall's top
   *  there (the underside), so `z1` may be Infinity — up to the wall's top. */
  underCeiling: boolean;
  /** It stands on a feature's top (a lintel, a header, a band over a pane or a
   *  doorway, a pocket's back): where the ceiling comes down below `z0` its
   *  bottom comes down with its top, so it has no height there. */
  over: boolean;
  /** Where the ceiling leaves it no height it is still capped at the wall's
   *  top (the wall over a feature), so a shell has no hole over it. */
  capped: boolean;
  stuff: PieceStuff;
}

const WALL: PieceStuff = { kind: 'wall' };

/** A thin rectangle centred on the segment a→b (total width `thick`). */
export function thinPanel(a: Vec2, b: Vec2, thick: number): Vec2[] {
  const dx = b.x - a.x, dy = b.y - a.y, L = Math.hypot(dx, dy) || 1;
  const nx = (-dy / L) * (thick / 2), ny = (dx / L) * (thick / 2);
  return [{ x: a.x + nx, y: a.y + ny }, { x: b.x + nx, y: b.y + ny }, { x: b.x - nx, y: b.y - ny }, { x: a.x - nx, y: a.y - ny }];
}

/** A door leaf's thickness in 3D (mm). */
const LEAF_THICK = 40;

const tag = (kind: SecPart['kind'], id: string | undefined): SecPart | null => (id ? { kind, id } : null);

/**
 * Every piece a room's panes, openings and doors make, panes first, then the
 * openings, then the doors (each in the room's order). Under a flat ceiling the
 * solid wall stops at `wallTop` (the ceiling's top, or its underside where the
 * ceiling is built apart — scene3d's choice), else at the room's top.
 */
export function roomFeaturePieces(inst: ProductInstance, opts: { wallTop?: number } = {}): FeaturePiece[] {
  const cf = isFlatCeiling(inst) ? null : resolveCeiling(inst);
  const out: FeaturePiece[] = [];
  const zExt = externalHeight(inst), zWall = opts.wallTop ?? zExt;
  const push: Push = (p, level = false) => {
    if (p.ring.length < 3) return;
    const underCeiling = !!cf && !level;
    // An empty piece is none (one up to a shaped ceiling's wall top has z1 Infinity).
    if (!(p.z1 > p.z0 + 1e-6)) return;
    out.push({ over: false, capped: false, ...p, underCeiling });
  };
  for (const seg of inst.glass ?? []) panePieces(inst, seg, cf, zExt, zWall, push);
  for (const seg of inst.openings ?? []) openingPieces(inst, seg, cf, zExt, zWall, push);
  for (const door of roomDoors(inst)) doorPieces(inst, door, cf, zExt, zWall, push);
  return out;
}

type Push = (p: Omit<FeaturePiece, 'underCeiling' | 'over' | 'capped'> & Partial<Pick<FeaturePiece, 'over' | 'capped'>>, level?: boolean) => void;

/** A pane: its glass from its own base to its own top, the wall under and
 *  over it — or, glazed into a partition, the partition's band under and over it. */
function panePieces(inst: ProductInstance, seg: GlassSegment, cf: CeilingField | null, zExt: number, zWall: number, push: Push): void {
  const feature = { kind: 'pane' as const, id: seg.id };
  // A glazing's pane is its glazing's (glazing.ts builds it): the wall stands
  // over the glazing's opening only.
  const gz = seg.innerWallId == null ? glazingOfPane(inst, seg.id) : null;
  if (gz) {
    const band = openingBandLocal(inst, seg), h = cf ? gz.input.H : Math.min(gz.input.H, zExt);
    if (band.length >= 3) push({ role: 'lintel', feature, part: null, ring: band, z0: cf ? h : Math.min(h, zWall), z1: cf ? Infinity : zWall, over: true, capped: true, stuff: WALL });
    return;
  }
  const top = cf ? glassPaneTop(inst, seg) : Math.min(glassPaneTop(inst, seg), zExt);
  const base = Math.min(glassPaneBase(seg), top);
  for (const ring of glassPolysLocal(inst, seg)) push({ role: 'glass', feature, part: tag('pane', seg.id), ring, z0: base, z1: top, stuff: { kind: 'glass', color: seg.color } });
  const band = openingBandLocal(inst, seg);
  if (band.length < 3) return;
  if (seg.innerWallId != null) {
    const iw = (inst.innerWalls ?? []).find((w) => w.id === seg.innerWallId);
    if (!iw) return;
    const stuff: PieceStuff = { kind: 'partition', iw }, part = tag('partition', iw.id);
    const iwBase = innerWallBase(iw), iwTop = cf ? innerWallTopAt(iw, Infinity) : innerWallTop(inst, iw);
    if (base > iwBase) push({ role: 'partitionBand', feature, part, ring: band, z0: iwBase, z1: Math.min(base, iwTop), stuff });
    push({ role: 'partitionBand', feature, part, ring: band, z0: Math.max(iwBase, top), z1: iwTop, over: true, stuff });
    return;
  }
  if (base > 0) push({ role: 'sill', feature, part: null, ring: band, z0: 0, z1: cf ? base : Math.min(base, zWall), stuff: WALL });
  push({ role: 'lintel', feature, part: null, ring: band, z0: cf ? top : Math.min(top, zWall), z1: cf ? Infinity : zWall, over: true, capped: true, stuff: WALL });
}

/** An opening: the wall under its sill and over its architrave, what a part's
 *  pocket leaves behind it, and the hole between them. */
function openingPieces(inst: ProductInstance, seg: GlassSegment, cf: CeilingField | null, zExt: number, zWall: number, push: Push): void {
  const feature = { kind: 'opening' as const, id: seg.id };
  const band = openingBandLocal(inst, seg);
  if (band.length < 3) return;
  const sill = cf ? openingSill(seg) : Math.min(openingSill(seg), zExt, zWall);
  const arch = cf ? openingArchitrave(inst, seg) : Math.min(openingArchitrave(inst, seg), zExt, zWall);
  push({ role: 'sill', feature, part: null, ring: band, z0: 0, z1: sill, stuff: WALL });
  push({ role: 'lintel', feature, part: null, ring: band, z0: arch, z1: cf ? Infinity : zWall, over: true, capped: true, stuff: WALL });
  for (const back of pocketBacks(inst, seg)) push({ role: 'pocketBack', feature, part: null, ring: back, z0: sill, z1: arch, over: true, stuff: WALL });
  push({ role: 'hole', feature, part: tag('opening', seg.id), ring: band, z0: sill, z1: cf ? Math.min(arch, minUnderOver(cf, band)) : arch, stuff: WALL }, true);
}

/** A door: its leaf, swung open as in the plan, a rigid panel whose top is
 *  level — the door's height, or the lowest the ceiling (or its partition)
 *  leaves its doorway —; the wall over its doorway (on a partition, the
 *  partition's band); and the doorway, as tall as the leaf. */
function doorPieces(inst: ProductInstance, door: SaunaDoor, cf: CeilingField | null, zExt: number, zWall: number, push: Push): void {
  const poly = isPolyRoom(inst);
  if (door.innerWallId != null && !poly) return;
  const rd = poly ? roomDoor(inst, door) : null;
  if (poly && !rd) return;
  const lp = rd ? { hinge: rd.swing.hinge, tip: rd.swing.open } : (({ hinge, leafTip }) => ({ hinge, tip: leafTip }))(doorParts(inst, door));
  const feature = { kind: 'door' as const, id: door.id };
  const leaf = thinPanel(lp.hinge, lp.tip, LEAF_THICK);
  const leafTop = cf ? doorTop(inst, door) : Math.min(doorTop(inst, door), zExt);
  const glass: PieceStuff = { kind: 'glass' };
  if (door.innerWallId != null) {
    const iw = (inst.innerWalls ?? []).find((w) => w.id === door.innerWallId);
    if (!iw) return;
    const band = rd?.gap ?? null;
    const iwTop = cf ? innerWallTopAt(iw, Infinity) : innerWallTop(inst, iw);
    const leafH = Math.min(leafTop, cf ? (band ? innerWallTopAt(iw, minUnderOver(cf, band)) : Infinity) : iwTop);
    push({ role: 'leaf', feature, part: tag('door', door.id), ring: leaf, z0: 0, z1: leafH, stuff: glass }, true);
    if (!band) return;
    const iwBase = innerWallBase(iw);
    push({ role: 'partitionBand', feature, part: tag('partition', iw.id), ring: band, z0: Math.max(iwBase, cf ? leafTop : Math.min(leafTop, iwTop)), z1: iwTop, over: true, stuff: { kind: 'partition', iw } });
    push({ role: 'hole', feature, part: tag('door', door.id), ring: band, z0: 0, z1: leafH, stuff: WALL }, true);
    return;
  }
  // The band the doorway cuts out of its wall, from the outer face to the inner.
  const gap = rd ? rd.gap : legacyDoorBand(inst, door);
  const leafH = cf && gap ? Math.min(leafTop, minUnderOver(cf, gap)) : leafTop;
  // A glazing's door is its glazing's (glazing.ts builds the leaf).
  if ((rd?.kind ?? 'plain') === 'plain') push({ role: 'leaf', feature, part: tag('door', door.id), ring: leaf, z0: 0, z1: leafH, stuff: glass }, true);
  if (!gap) return;
  push({ role: 'header', feature, part: null, ring: gap, z0: cf ? leafTop : Math.min(leafTop, zWall), z1: cf ? Infinity : zWall, over: true, capped: true, stuff: WALL });
  push({ role: 'hole', feature, part: tag('door', door.id), ring: gap, z0: 0, z1: leafH, stuff: WALL }, true);
}

/** A legacy rectangular room's doorway: its front wall's band over the door. */
function legacyDoorBand(inst: ProductInstance, door: SaunaDoor): Vec2[] | null {
  const seg = doorGlassSpans(inst).find((d) => d.door === door)?.seg;
  const band = seg ? openingBandLocal(inst, seg) : [];
  return band.length >= 3 ? band : null;
}

/** A piece's bottom and top at a plan point (local), under a shaped ceiling
 *  (`cf`) or a flat one (null) — what its mesh is built between. */
export function pieceSpanAt(p: FeaturePiece, cf: CeilingField | null): { bottom: (q: Vec2) => number; top: (q: Vec2) => number } {
  if (!cf || !p.underCeiling) return { bottom: () => p.z0, top: () => p.z1 };
  const top = (q: Vec2): number => Math.min(p.z1, wallTopAt(cf, q));
  return { bottom: p.over ? (q) => Math.min(p.z0, top(q)) : () => p.z0, top };
}

/** What a click in the 3D view may take where no mesh is: a vertical prism over
 *  a convex world plan ring, between two heights above the ground. */
export interface PickVolume { src: SecSrc; ring: Vec2[]; z0: number; z1: number }

/** A room's holes as pick volumes — its openings (sill to architrave) and its
 *  doorways (the floor to the leaf's top), each as tall as its piece: with the
 *  room selected, a click through one picks it, as the plan's click on its band
 *  does. Features with no id yet are left out (they are the room's). */
export function featureVolumes(inst: ProductInstance): PickVolume[] {
  if (!inst.autoBenches || !isPolyRoom(inst)) return [];
  const lift = roomBase(inst);
  return roomFeaturePieces(inst).filter((p) => p.role === 'hole' && p.part).map((p) => ({
    src: { id: inst.id, part: p.part! },
    ring: p.ring.map((q) => instanceToWorld(inst, q)),
    z0: lift + p.z0,
    z1: lift + p.z1,
  }));
}
